import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from 'contentful';
import type { TypeBlockClientTestimonialSkeleton } from './TypeBlockClientTestimonial';
import type { TypeBlockDarkCircleBackgroundSkeleton } from './TypeBlockDarkCircleBackground';
import type { TypeBlockFeaturedCaseStudiesSkeleton } from './TypeBlockFeaturedCaseStudies';
import type { TypeBlockIconBlocksSkeleton } from './TypeBlockIconBlocks';
import type { TypeBlockImageSkeleton } from './TypeBlockImage';
import type { TypeBlockImageWithPartialOverlaySkeleton } from './TypeBlockImageWithPartialOverlay';
import type { TypeBlockIndustryBlocksSkeleton } from './TypeBlockIndustryBlocks';
import type { TypeBlockLogoGridSkeleton } from './TypeBlockLogoGrid';
import type { TypeBlockNumberedBlocksSkeleton } from './TypeBlockNumberedBlocks';
import type { TypeBlockPageSectionSkeleton } from './TypeBlockPageSection';
import type { TypeBlockPeopleListSkeleton } from './TypeBlockPeopleList';
import type { TypeBlockSlideshowSkeleton } from './TypeBlockSlideshow';
import type { TypeBlockTechnologyBlocksSkeleton } from './TypeBlockTechnologyBlocks';
import type { TypeClientSkeleton } from './TypeClient';
import type { TypeCtaSimpleSkeleton } from './TypeCtaSimple';
import type { TypeImageWrapperSkeleton } from './TypeImageWrapper';
import type { TypeIndustrySkeleton } from './TypeIndustry';
import type { TypeServiceSkeleton } from './TypeService';
import type { TypeSolutionSkeleton } from './TypeSolution';
import type { TypeTechnologySkeleton } from './TypeTechnology';

export interface TypePageCaseStudyFields {
    internalName: EntryFieldTypes.Symbol;
    slug: EntryFieldTypes.Symbol;
    seoTitle?: EntryFieldTypes.Symbol;
    seoDescription?: EntryFieldTypes.Symbol;
    title: EntryFieldTypes.Symbol;
    year?: EntryFieldTypes.Integer;
    client?: EntryFieldTypes.EntryLink<TypeClientSkeleton>;
    summary: EntryFieldTypes.Text;
    publishDate?: EntryFieldTypes.Date;
    featured?: EntryFieldTypes.Boolean;
    isSnapshot?: EntryFieldTypes.Boolean;
    listImage?: EntryFieldTypes.EntryLink<TypeImageWrapperSkeleton>;
    heroImage?: EntryFieldTypes.EntryLink<TypeImageWrapperSkeleton>;
    challenge?: EntryFieldTypes.Text;
    approach?: EntryFieldTypes.Text;
    outcome?: EntryFieldTypes.Text;
    clientIntroductionHeader?: EntryFieldTypes.Symbol;
    clientIntroductionBody?: EntryFieldTypes.Text;
    clientIntroductionImage?: EntryFieldTypes.EntryLink<TypeImageWrapperSkeleton>;
    services?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeServiceSkeleton>>;
    solutions?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeSolutionSkeleton>>;
    technologies?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeTechnologySkeleton>>;
    industries?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeIndustrySkeleton>>;
    sections?: EntryFieldTypes.Array<
        EntryFieldTypes.EntryLink<
            | TypeBlockClientTestimonialSkeleton
            | TypeBlockDarkCircleBackgroundSkeleton
            | TypeBlockFeaturedCaseStudiesSkeleton
            | TypeBlockIconBlocksSkeleton
            | TypeBlockImageSkeleton
            | TypeBlockImageWithPartialOverlaySkeleton
            | TypeBlockIndustryBlocksSkeleton
            | TypeBlockLogoGridSkeleton
            | TypeBlockNumberedBlocksSkeleton
            | TypeBlockPageSectionSkeleton
            | TypeBlockPeopleListSkeleton
            | TypeBlockSlideshowSkeleton
            | TypeBlockTechnologyBlocksSkeleton
            | TypeCtaSimpleSkeleton
        >
    >;
    slideshowPage?: EntryFieldTypes.EntryLink<TypeServiceSkeleton | TypeSolutionSkeleton | TypeTechnologySkeleton>;
}

export type TypePageCaseStudySkeleton = EntrySkeletonType<TypePageCaseStudyFields, 'pageCaseStudy'>;
export type TypePageCaseStudy<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<
    TypePageCaseStudySkeleton,
    Modifiers,
    Locales
>;

export function isTypePageCaseStudy<Modifiers extends ChainModifiers, Locales extends LocaleCode>(
    entry: Entry<EntrySkeletonType, Modifiers, Locales>
): entry is TypePageCaseStudy<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'pageCaseStudy';
}
